import * as z from 'zod';

export const CreateInviteRequest = z.object({
  subjectsAssignments: z
    .array(
      z.object({
        subjectEmail: z
          .string()
          .describe("Invitee's email address.")
          .email()
          .optional(),
        assignments: z
          .array(
            z.object({
              policyId: z.string().describe('Role ID.').optional(),
              assignments: z
                .array(
                  z.object({
                    fullNameResource: z
                      .intersection(
                        z.object({}),
                        z.xor([
                          z.object({
                            siteContext: z.never().optional(),
                            accountContext: z.never().optional(),
                          }),
                          z.object({
                            accountContext: z.never().optional(),
                            siteContext: z
                              .object({
                                metasiteId: z
                                  .string()
                                  .describe('Site ID.')
                                  .optional(),
                              })
                              .describe('Specific site details.'),
                          }),
                          z.object({
                            siteContext: z.never().optional(),
                            accountContext: z
                              .object({
                                accountId: z
                                  .string()
                                  .describe('Account ID.')
                                  .optional(),
                              })
                              .describe('Specific account details.'),
                          }),
                        ])
                      )
                      .describe('Full name of resource to be assigned.')
                      .optional(),
                  })
                )
                .optional(),
            })
          )
          .optional(),
      })
    )
    .min(1)
    .max(50),
  options: z
    .object({
      defaultEmailLanguage: z
        .string()
        .describe(
          "Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language."
        )
        .optional()
        .nullable(),
    })
    .describe('Filter options.')
    .optional(),
});
export const CreateInviteResponse = z.object({
  successfulInvites: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Invite ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        accountId: z
          .string()
          .describe('Account ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        email: z
          .string()
          .describe('Email address where the invite was sent.')
          .email()
          .optional(),
        role: z.string().describe('Deprecated. Use `policyIds`.').optional(),
        inviterId: z
          .string()
          .describe('Deprecated. Use `inviterAccountId`.')
          .optional(),
        status: z
          .enum(['Pending', 'Used', 'Deleted', 'Declined', 'Expired'])
          .describe(
            "Invite status.\n\nSupported values:\n- **Pending:** The invite has been sent and is valid, waiting for the user's response.\n- **Used:** The invite has been accepted.\n- **Deleted:** The invite has been deleted or revoked.\n- **Declined:** The user has declined the invite.\n- **Expired:** The invite has expired without being accepted."
          )
          .optional(),
        acceptLink: z
          .string()
          .describe('Link to accept the invite.')
          .optional(),
        inviterAccountId: z
          .string()
          .describe('Inviting account ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        acceptedByAccountId: z
          .string()
          .describe(
            'Account ID that accepted the invite. Populated only once the invite is accepted.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        dateCreated: z
          .date()
          .describe('Date the invite was created.')
          .optional()
          .nullable(),
        policyIds: z.array(z.string()).optional(),
        dateUpdated: z
          .date()
          .describe('Date the invite was last updated.')
          .optional()
          .nullable(),
        assignments: z
          .array(
            z.object({
              policyId: z.string().describe('Role ID.').optional(),
              assignments: z
                .array(
                  z.object({
                    fullNameResource: z
                      .intersection(
                        z.object({}),
                        z.xor([
                          z.object({
                            siteContext: z.never().optional(),
                            accountContext: z.never().optional(),
                          }),
                          z.object({
                            accountContext: z.never().optional(),
                            siteContext: z
                              .object({
                                metasiteId: z
                                  .string()
                                  .describe('Site ID.')
                                  .optional(),
                              })
                              .describe('Specific site details.'),
                          }),
                          z.object({
                            siteContext: z.never().optional(),
                            accountContext: z
                              .object({
                                accountId: z
                                  .string()
                                  .describe('Account ID.')
                                  .optional(),
                              })
                              .describe('Specific account details.'),
                          }),
                        ])
                      )
                      .describe('Full name of resource to be assigned.')
                      .optional(),
                  })
                )
                .optional(),
            })
          )
          .optional(),
        expirationDate: z
          .date()
          .describe('Invite expiration date.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  failedInvites: z
    .array(
      z.object({
        subjectEmail: z
          .string()
          .describe('Email address of the failed invite.')
          .email()
          .optional(),
        errorMessage: z.string().describe('Error description.').optional(),
      })
    )
    .optional(),
});
